/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';

import { colors, fonts, mixins } from '@blend/consumer-components';

import { addNotifications } from '../../../actions/pageActions';
import { notaryAPI } from '../../../api/notary';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { NotificationItem } from '../../common/Notifications/NotificationItem';
import { ConfirmationModal } from '../../common/Modals/ConfirmationModal/ConfirmationModal';
import { Spinner } from '../../../pages/TasksPage/Spinner';
import SigningDocument from '../../common/Documents/SigningDocument';

export namespace ResetDocumentConfirmationModal {
  export interface Props {
    isModalOpen: boolean;
    handleModalClose: () => void;
    document: SigningDocument;
  }
}

const styles = StyleSheet.create({
  text: {
    fontSize: 16,
    lineHeight: '22px',
    ...fonts.UntitledSans,
  },
  bold: {
    fontWeight: fonts.weight.bold,
  },
  spinner: {
    textAlign: 'center',
    ...mixins.margin(20, 0, 0, 0),
  },
});

export const ResetDocumentConfirmationModal: React.FC<ResetDocumentConfirmationModal.Props> = ({
  isModalOpen,
  handleModalClose,
  document,
}) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { callDetails } = useRootSelector(state => ({ callDetails: state.callDetails }));
  const { id } = document;

  const resetDocument = async () => {
    try {
      setIsSubmitting(true);
      await notaryAPI.documents.resetDocument(callDetails!.id, id);
    } catch (err) {
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content: (err as Error)?.message,
          shouldAutoRemove: true,
        },
      ];
      addNotifications(notifications)(dispatch);
    }

    setIsSubmitting(false);
    handleModalClose();
  };

  const resetTargetText = 'all documents';
  const headingText = 'Do you want to reset this document package?';

  return (
    <ConfirmationModal
      isOpen={isModalOpen}
      isDisabled={isSubmitting}
      contentLabel={'Reset Document'}
      heading={headingText}
      confirmButtonText={'Yes'}
      handleConfirm={resetDocument}
      handleClose={handleModalClose}
      closeButtonText={'Cancel'}
      buttonColor={colors.reflex}
      data-it="ResetDocumentConfirmationModal"
    >
      <div className={css(styles.text)} data-it="ResetDocumentConfirmationModal-text">
        If you say yes, <strong className={css(styles.bold)}>{resetTargetText}</strong> will be reset to an earlier
        version. This version will include all tags that were placed prior to the start of signing.
        <br />
        <strong className={css(styles.bold)}>This action cannot be undone.</strong>
      </div>
      {isSubmitting && (
        <div className={css(styles.spinner)}>
          <Spinner />
          <p>Document package resetting...</p>
        </div>
      )}
    </ConfirmationModal>
  );
};
